import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { studentAPI } from '../../services/api';
import ProjectTest from './ProjectTest';

function ProjectDetails({ project, onBack, user }) {
    const { project: projectInfo, submission, testResult, certificate } = project;
    const [githubLink, setGithubLink] = useState(submission?.githubLink || '');
    const [submitting, setSubmitting] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [stepProgress, setStepProgress] = useState({ completedSteps: [], totalSteps: 0 });
    const [loadingStep, setLoadingStep] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await studentAPI.submitProject(projectInfo._id, githubLink);
            toast.success('Project submitted successfully! Admin will review it soon.');
            setTimeout(() => onBack(), 1500);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadCertificate = async () => {
        setDownloading(true);
        try {
            const response = await studentAPI.downloadCertificate(projectInfo._id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `certificate-${projectInfo.name}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Certificate downloaded!');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const fetchStepProgress = async () => {
        try {
            const response = await studentAPI.getStepProgress(projectInfo._id);
            setStepProgress(response.data);
        } catch (error) {
            console.error('Failed to fetch step progress');
        }
    };

    const handleCompleteStep = async (stepNumber) => {
        setLoadingStep(stepNumber);
        try {
            const response = await studentAPI.completeStep(projectInfo._id, stepNumber);
            setStepProgress({
                completedSteps: response.data.completedSteps,
                totalSteps: response.data.totalSteps,
            });
            toast.success(`Step ${stepNumber} completed! 🎉`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to mark step as complete');
        } finally {
            setLoadingStep(null);
        }
    };

    // Fetch step progress on mount
    React.useEffect(() => {
        if (projectInfo?.steps && projectInfo.steps.length > 0) {
            fetchStepProgress();
        }
        // eslint-disable-next-line
    }, [projectInfo._id]);

    if (showTest) {
        return (
            <ProjectTest
                projectId={projectInfo._id}
                projectName={projectInfo.name}
                onBack={() => setShowTest(false)}
                onComplete={onBack}
            />
        );
    }

    return (
        <div className="project-details">
            <button onClick={onBack} className="btn btn-back">
                ← Back to Projects
            </button>

            <div className="project-header">
                <h2>{projectInfo.name}</h2>
                <div className="project-meta">
                    <span className="badge">{projectInfo.category}</span>
                    <span className="badge">{projectInfo.type}</span>
                </div>
            </div>

            {projectInfo.description && (
                <div className="section">
                    <h3>Description</h3>
                    <p>{projectInfo.description}</p>
                </div>
            )}

            {projectInfo.documentation && (
                <div className="section">
                    <h3>Documentation</h3>
                    <a
                        href={`http://localhost:5000/${projectInfo.documentation}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        📄 View Documentation
                    </a>
                </div>
            )}

            {projectInfo.driveLink && (
                <div className="section">
                    <h3>📂 Google Drive Resources</h3>
                    <a
                        href={projectInfo.driveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        📁 Open Drive Folder
                    </a>
                </div>
            )}

            {projectInfo.steps && projectInfo.steps.length > 0 && (
                <div className="section step-tracker-section">
                    <h3>📋 Project Steps</h3>

                    {/* Progress Bar */}
                    <div className="progress-bar-container">
                        <div className="progress-info">
                            <span>Progress: {stepProgress.completedSteps.length}/{stepProgress.totalSteps} Steps</span>
                            <span>{stepProgress.totalSteps > 0 ? Math.round((stepProgress.completedSteps.length / stepProgress.totalSteps) * 100) : 0}%</span>
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: `${stepProgress.totalSteps > 0 ? (stepProgress.completedSteps.length / stepProgress.totalSteps) * 100 : 0}%`
                                }}
                            ></div>
                        </div>
                    </div>

                    {/* Steps List */}
                    <div className="steps-tracker">
                        {projectInfo.steps.map((step, index) => {
                            const stepNumber = index + 1;
                            const isCompleted = stepProgress.completedSteps.includes(stepNumber);
                            const isUnlocked = stepNumber === 1 || stepProgress.completedSteps.includes(stepNumber - 1);
                            const isActive = isUnlocked && !isCompleted;

                            return (
                                <div
                                    key={index}
                                    className={`step-item ${isCompleted ? 'completed' : isUnlocked ? 'unlocked' : 'locked'
                                        }`}
                                >
                                    <div className="step-header">
                                        <div className="step-icon">
                                            {isCompleted ? '✅' : isUnlocked ? '🔓' : '🔒'}
                                        </div>
                                        <div className="step-content">
                                            <h4>Step {stepNumber}: {step.title}</h4>
                                            <p>{step.description}</p>
                                        </div>
                                    </div>
                                    {isActive && (
                                        <button
                                            onClick={() => handleCompleteStep(stepNumber)}
                                            className="btn btn-success btn-sm"
                                            disabled={loadingStep === stepNumber}
                                        >
                                            {loadingStep === stepNumber ? '⏳ Marking...' : '✓ Mark as Done'}
                                        </button>
                                    )}
                                    {isCompleted && (
                                        <span className="completed-badge">Completed ✓</span>
                                    )}
                                    {!isUnlocked && (
                                        <span className="locked-badge">Locked</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {projectInfo.downloadLink && (
                <div className="section">
                    <h3>Full Project Download</h3>
                    <a
                        href={projectInfo.downloadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        ⬇️ Download Project Files
                    </a>
                </div>
            )}

            <div className="section submission-section">
                <h3>Submit Your Work</h3>

                {!submission || submission.status === 'rejected' ? (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="githubLink">GitHub Repository Link</label>
                            <input
                                type="url"
                                id="githubLink"
                                value={githubLink}
                                onChange={(e) => setGithubLink(e.target.value)}
                                placeholder="https://github.com/username/repository"
                                required
                            />
                            <small>
                                Make sure your repository is public and accessible
                            </small>
                        </div>

                        {submission?.status === 'rejected' && (
                            <div className="alert alert-warning">
                                Your previous submission was rejected. Please review the feedback and resubmit.
                                {submission.reviewNotes && <p><strong>Notes:</strong> {submission.reviewNotes}</p>}
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? 'Submitting...' : submission ? 'Resubmit Project' : 'Submit Project'}
                        </button>
                    </form>
                ) : (
                    <div className="submission-status">
                        <p>
                            <strong>Status:</strong>{' '}
                            <span className={`badge badge-${submission.status}`}>
                                {submission.status.toUpperCase()}
                            </span>
                        </p>
                        <p>
                            <strong>GitHub Link:</strong>{' '}
                            <a href={submission.githubLink} target="_blank" rel="noopener noreferrer">
                                {submission.githubLink}
                            </a>
                        </p>
                        <p>
                            <strong>Submitted:</strong> {new Date(submission.submittedAt).toLocaleString()}
                        </p>
                        {submission.reviewNotes && (
                            <p>
                                <strong>Admin Notes:</strong> {submission.reviewNotes}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {submission?.status === 'accepted' && (
                <div className="section test-section">
                    <h3>Project Test</h3>
                    {testResult?.passed ? (
                        <div className="test-result success">
                            <p>✅ You passed the test with {testResult.score}%!</p>
                            {certificate ? (
                                <button
                                    onClick={handleDownloadCertificate}
                                    className="btn btn-success"
                                    disabled={downloading}
                                >
                                    {downloading ? 'Downloading...' : '🎓 Download Certificate'}
                                </button>
                            ) : (
                                <p className="info-message">
                                    Your certificate is being prepared by the admin. Please check back later.
                                </p>
                            )}
                        </div>
                    ) : testResult ? (
                        <div className="test-result failure">
                            <p>❌ You scored {testResult.score}% (Required: 80%)</p>
                            <p>Don't worry! You can retake the test.</p>
                            <button onClick={() => setShowTest(true)} className="btn btn-primary">
                                Retake Test
                            </button>
                        </div>
                    ) : (
                        <div className="test-info">
                            <p>Your submission has been accepted! Take the test to earn your certificate.</p>
                            <p><strong>Test Details:</strong></p>
                            <ul>
                                <li>10 interview questions related to your project</li>
                                <li>Must score 80% or higher to pass</li>
                                <li>You can retake the test if you don't pass</li>
                            </ul>
                            <button onClick={() => setShowTest(true)} className="btn btn-primary">
                                Start Test
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProjectDetails;
